import { DEFAULT_LIMIT, DEFAULT_SKIP, LIMIT } from 'Src/consts/table';
import { Count } from 'Src/models/count';
import { superAdminDelete, superAdminGet, superAdminPost, superAdminPut } from './api';

export interface SuperAdminSchoolsFilter {
  limit?: number;
  skip?: number;
  order?: string;
  where?: any;
}

export interface SuperAdminSchoolUpdateData {
  name?: string;
  description?: string;
  pic?: string;
  address?: string;
  postcodeId?: string;
  phone?: string;
  email?: string;
  notificationEmail?: string;
  sportsDepartmentEmail?: string;
  status?: string;
  subscriptionPlan?: string;
  domain?: string | null;
  commentsEnabled?: boolean;
  photosEnabled?: boolean;
  settings?: {
    commentsEnabled?: boolean;
    photosEnabled?: boolean;
    availabilityStudentEnabled?: boolean;
    availabilityParentEnabled?: boolean;
  };
  studentImportForAdminAllowed?: boolean;
  canEditFavoriteSports?: boolean;
  availableForRegistration?: boolean;
  studentSelfRegistrationEnabled?: boolean;
  publicStudentViewType?: string;
  notificationsEnabled?: boolean;
  allowedForFollowing?: boolean;
  eventPerformanceEnabledForParent?: boolean;
	eventCommunicationsAvailable?: boolean;
	tournamentCommunicationsAvailable?: boolean;
  ageGroupsNaming?: string;
  isClubsEnabled?: boolean;
  isClubPriorityEnabled?: boolean;
  isAbsenceRequestEnabled?: boolean;
  clubInvitations?: string;
  isPaymentsEnabled?: boolean;
  applicationFeePercentage?: number;
  canPublishWebSite?: boolean;
  canAcceptStaffRoles?: boolean;
  isFavoriteSportsEnabled?: boolean;
  canCreateStudentFromParentPermissionRequest?: boolean;
  isShowSchoolSelectOnPublicSite?: boolean;
  isShowLeagueTableOnPublicSite?: boolean;
  isShowSchoolTableOnPublicSite?: boolean;
  isShowTournamentsOnPublicSite?: boolean;
  isCreateSmsNotificationChannelForNewUsers?: boolean;
  isSendSmsWithAllSystemEvents?: boolean;
  isGeneralMessagesEnabled?: boolean;
  generalMessagesMode?: 'classic' | 'v2';
  isFirstAdminAutoAccept?: boolean;
  isParentsAndStudentsAutoAccept?: boolean;
  isCoachSeenOnlyAffectedEvents?: boolean;
  isTeacherSeenOnlyAffectedEvents?: boolean;
  isOneOffCodeOnRegisterEnabled?: boolean;
  isEventUpdateNotificationCustomMessageEnabled?: boolean;
  isFollowersAutoenroll?: boolean;
  disclaimer?: string;
  canSellPhotos?: boolean;
  shortCode?: string;
  isShowStudentEventClashes?: boolean;
  emailFooterData?: string;
  emailGeneralMessageFooterData?: string;
  region?: string;
  county?: string;
  eduPhase?: string;
  gender?: string;
  twitterAccountName?: string;
  photoAccessPreset?: string;
  isShowAggregatedHouseLeagueOnPublicWebsite?: boolean;
  availableAges?: number[];
  availableGender?: string;
  allowedPermissionPresets?: { [key: string]: boolean };
  additionalPermissionRequestFields?: {
    childFirstName?: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
    childLastName?: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
    childDateOfBirth?: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
    childGender?: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
    childForm?: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
    childHouse?: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
  };
  triggers?: {
    onEventInviteAction?: string;
    onTournamentInviteAction?: string;
    onTournamentEventInviteAction?: string;
    onInviteEmailNotification?: boolean;
  };
  getStartedWizardStep?: number;
  allowedParentRelationships?: string[];
  parentAutoLinkMatchMode?: string;
  gmEmailHeaderColor?: string;
  gmEmailHeaderEnabled?: boolean;
  gmEmailHeaderLogoEnabled?: boolean;
  gmEmailHeaderNameEnabled?: boolean;
  gmEmailHeaderLogoAlignment?: 'left' | 'center' | 'right';
  gmEmailHeaderNamePlacement?: 'above' | 'left' | 'right' | 'below';
  gmEmailHeaderNameAlignment?: 'left' | 'center' | 'right';
  gmEmailFooterHtml?: string;
  gmEmailFooterBackgroundColor?: string;
  gmEmailFooterTextColor?: string;
  gmEmailFooterAlignment?: 'left' | 'center' | 'right';
}

export interface SuperAdminSchoolCreateData extends SuperAdminSchoolUpdateData {
  kind?: string;
}

const DEFAULT_FILTER: SuperAdminSchoolsFilter = {
  limit: LIMIT,
  skip: DEFAULT_SKIP,
  order: 'name ASC'
};

function buildFilter(filter: SuperAdminSchoolsFilter = DEFAULT_FILTER): SuperAdminSchoolsFilter {
  return {
    where: filter.where || {},
    skip: typeof filter.skip === 'number' ? filter.skip : DEFAULT_FILTER.skip,
    limit: typeof filter.limit === 'number' ? filter.limit : DEFAULT_FILTER.limit,
    order: filter.order || DEFAULT_FILTER.order
  };
}

export function getSuperAdminSchools(filter: SuperAdminSchoolsFilter = DEFAULT_FILTER): Promise<any[]> {
  return superAdminGet<any[]>('/schools', {
    params: {
      filter: buildFilter(filter)
    }
  });
}

export function getAllSuperAdminSchools(where?: any): Promise<any[]> {
  return superAdminGet<any[]>('/schools', {
    params: {
      filter: {
        where: where || {},
        skip: DEFAULT_SKIP,
        limit: DEFAULT_LIMIT,
        order: DEFAULT_FILTER.order
      }
    }
  });
}

export function getSuperAdminSchoolsCount(where?: any): Promise<Count> {
  const config =
    typeof where !== 'undefined'
      ? {
          params: {
            filter: {
              where
            }
          }
        }
      : {};

  return superAdminGet<Count>('/schools/count', config);
}

export function getSuperAdminSchool(schoolId: string): Promise<any> {
  return superAdminGet<any>(`/schools/${schoolId}`);
}

export function createSuperAdminSchool(data: SuperAdminSchoolCreateData): Promise<any> {
  return superAdminPost<any>('/schools', data);
}

export function updateSuperAdminSchool(schoolId: string, data: SuperAdminSchoolUpdateData): Promise<any> {
  return superAdminPut<any>(`/schools/${schoolId}`, data);
}

export function updateSuperAdminSchoolStatus(schoolId: string, status: string): Promise<any> {
  return superAdminPut<any>(`/schools/${schoolId}/status`, { status });
}

export function deleteSuperAdminSchool(schoolId: string): Promise<any> {
  return superAdminDelete<any>(`/schools/${schoolId}`);
}

export function updateSuperAdminSchoolModule(schoolId: string, module: string, data: any): Promise<any> {
  return superAdminPut<any>(`/schools/${schoolId}/modules/${module}`, data);
}

export function setSuperAdminSchoolAccessPreset(
  schoolId: string,
  presets: string[],
  canAcceptStaffRoles: boolean,
  canPublishWebSite: boolean
): Promise<any> {
  return superAdminPut<any>(`/schools/${schoolId}/access-preset`, {
    presets,
    canAcceptStaffRoles,
    canPublishWebSite
  });
}
